import produce from 'immer';
import _ from 'lodash';
import {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useLazyQuery} from 'react-apollo';

import {
  DefaultResourceConfig,
  FetchEntityJobsQuery,
  FetchProjectJobsPartialDocument,
  FetchProjectJobsPartialQuery,
  FetchProjectJobsPartialQueryResult,
  PageInfo,
  ProjectEdge,
  RampIdType,
  useAllProjectNamesWithPatternQuery,
  useAllQueuesLiteQuery,
  useEntityQueueByQueueNameLiteQuery,
  useFetchEntityJobsQuery,
  useFetchProjectLaunchQueuesQuery,
  useFetchRunQueueItemFromProjectQuery,
  useRunJobQuery,
  useUserRunsQuery,
  useViewerAllProjectsWithPatternQuery,
  useViewerOrganizationFeatureFlagsQuery,
  useViews2MetadataQuery,
  useViews2RawViewQuery,
} from '../../generated/graphql';
import {useProjectParams} from '../../pages/ProjectPage/useProjectParams';
import {useViewer} from '../../state/viewer/hooks';
import {propagateErrorsContext} from '../../util/errors';
import {useRampFlagAccountSelector} from '../../util/rampFeatureFlags';
import {AccountSelectorContext} from '../Search/SearchNav/AccountSelectorContextProvider';
import {DEFAULT_LAUNCH_PROJECT_NAME} from './constants';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {
  enhanceRunQueueItems,
  getProjectRunIdMap,
  getRunInformation,
  isRecentlyEnqueued,
  makeQueueOptionsFromPublicQueueQuery,
  makeQueueOptionsFromQueueQueries,
  Queue,
  RunInfoMap,
} from './Queues/queueUtil';
import {
  mapToPopoverItems,
  RunInfoWithProjectAndEntityNames,
  sortUserRunQueueItemsWithRecentRuns,
} from './runQueuePopoverUtil';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {JobOption} from './SelectJob';
import {CREATE_QUEUE_PLACEHOLDER, QueueOptions} from './SelectQueue';
import {RunQueueItem} from './types';
import {
  constructQueueOptions,
  isAllowedWandbPublicJob,
  isWandbJobsProject,
  PUBLIC_QUEUE_RAMP_KEY,
  sortQueues,
  WANDB_ENTITY_NAME,
  WANDB_JOBS_PROJECT_NAME,
  WANDB_PUBLIC_QUEUE_NAME,
} from './utils';

type AccessorFunc = (a: any) => string;
const sortSpecialFirst = (special: string, accessor: AccessorFunc) => {
  return (a: any, b: any) => {
    const l1 = accessor(a);
    const l2 = accessor(b);
    if (l1 === l2) {
      return 0;
    }
    if (l1 === special) {
      return -1;
    }
    if (l2 === special) {
      return 1;
    }
    return caseInsensitiveCompare(l1, l2);
  };
};

export const caseInsensitiveCompare = (a: string, b: string) => {
  const lowerA = a.toLocaleLowerCase();
  const lowerB = b.toLocaleLowerCase();
  return lowerA.localeCompare(lowerB);
};

// Query for the available launch queues and return a sorted array in the select widget format.
export const useEntityQueueOptions = (
  entityName: string | undefined,
  projectName: string | undefined,
  defaultResourceConfigs?: Record<string, DefaultResourceConfig>,
  includeCreate: boolean = false
) => {
  const configs = defaultResourceConfigs ?? {};
  const projectRunQueuesQuery = useFetchProjectLaunchQueuesQuery({
    variables: {
      entityName: entityName ?? '',
      projectName: projectName ?? '',
    },
    skip: entityName == null,
  });
  const skipEntityQuery =
    projectName === DEFAULT_LAUNCH_PROJECT_NAME || entityName == null;
  const entityRunQueuesQuery = useFetchProjectLaunchQueuesQuery({
    variables: {
      entityName: entityName ?? '',
      projectName: DEFAULT_LAUNCH_PROJECT_NAME,
    },
    skip: skipEntityQuery,
  });
  const projectQueues = constructQueueOptions(projectRunQueuesQuery, configs);
  sortQueues(projectQueues);

  const entityQueues = constructQueueOptions(entityRunQueuesQuery, configs);
  sortQueues(entityQueues);
  let queues: QueueOptions = [];
  if (
    entityQueues.length === 0 ||
    projectName === DEFAULT_LAUNCH_PROJECT_NAME
  ) {
    queues = projectQueues;
    if (queues.length === 0 && includeCreate) {
      queues.push(CREATE_QUEUE_PLACEHOLDER);
    }
  } else if (projectQueues.length === 0) {
    queues = entityQueues;
    if (queues.length === 0 && includeCreate) {
      queues.push(CREATE_QUEUE_PLACEHOLDER);
    }
  } else {
    queues = [
      {
        label: 'Launch',
        options: entityQueues,
      },
      {
        label: projectName ?? '',
        options: projectQueues,
      },
    ];
  }

  return {
    queues,
    loading: projectRunQueuesQuery.loading || entityRunQueuesQuery.loading,
    error: projectRunQueuesQuery.error || entityRunQueuesQuery.error,
  };
};

// Alternate "GetQueueOptions" (renamed to EntityQueueOptions) that shows all queues, not just the ones for the project.
export const useViewerQueueOptions = (
  jobIsPrivate: boolean,
  currentEntity: string,
  filterInactive: boolean = true
) => {
  const viewer = useViewer();
  const personalEntity = viewer?.username ?? '';
  const enableAccountSelector = useRampFlagAccountSelector();
  const {selectedAccount} = useContext(AccountSelectorContext);
  const {hasPublicQueueAccess, isFeatureFlagLoading} = usePublicQueueAccess();

  const allQueuesQuery = useAllQueuesLiteQuery({pollInterval: 5000});
  const publicQueuesQuery = useEntityQueueByQueueNameLiteQuery({
    variables: {
      entityName: WANDB_ENTITY_NAME,
      queueName: WANDB_PUBLIC_QUEUE_NAME,
    },
    skip: !hasPublicQueueAccess,
  });

  if (!allQueuesQuery && !publicQueuesQuery) {
    return {
      queues: [],
      loading: false,
      error: null,
    };
  }

  if (allQueuesQuery.loading) {
    return {
      queues: [],
      loading: true,
      error: null,
    };
  }

  if (publicQueuesQuery.loading) {
    return {
      queues: [],
      loading: true,
      error: null,
    };
  }

  let publicQueueOptions = makeQueueOptionsFromPublicQueueQuery(
    publicQueuesQuery,
    filterInactive
  );

  // prevent the public queue from being shown twice to wandb members
  if (currentEntity === 'wandb' && viewer?.entity === 'wandb') {
    publicQueueOptions = [];
  }

  const queueOptions = makeQueueOptionsFromQueueQueries(
    allQueuesQuery,
    filterInactive,
    jobIsPrivate,
    currentEntity,
    personalEntity,
    enableAccountSelector && selectedAccount ? selectedAccount : undefined
  );

  return {
    queues: [...queueOptions, ...publicQueueOptions],
    loading:
      allQueuesQuery.loading ||
      publicQueuesQuery.loading ||
      isFeatureFlagLoading,
    error: allQueuesQuery.error || publicQueuesQuery.error,
  };
};

// Runs have a default color, but user can set manually.
// This hook extracts the project's view spec and run color information.
export const useProjectView = (
  entityName: string | undefined,
  projectName: string | undefined
) => {
  const queryViewMetadata = useViews2MetadataQuery({
    variables: {
      entityName,
      name: projectName,
      viewType: 'project-view',
    },
    skip: entityName == null || projectName == null,
  });
  const viewEdges = queryViewMetadata.data?.project?.allViews?.edges ?? [];
  let viewId = '';
  if (viewEdges.length > 0) {
    viewId = viewEdges[0].node?.id ?? '';
  }

  const queryRawView = useViews2RawViewQuery({
    variables: {
      id: viewId,
    },
    skip: !viewId,
  });

  const spec = queryRawView.data?.view?.specObject ?? {};
  const customRunColors = spec?.section?.customRunColors ?? {};

  return {
    viewId,
    spec,
    customRunColors,
    loading: queryViewMetadata.loading || queryRawView.loading,
    error: queryViewMetadata.error || queryRawView.error,
  };
};

/**
 * This function will load destination project options based on the queue type
 *
 * For public queues, load up the viewer's projects because they can launch
 * into their own projects.
 *
 * Otherwise, for normal queues, load up the queue entity's projects.
 * */
// We pass the entity/project we are in so we can sort them first.
export const useDestinationProjectOptions = (
  projectName: string,
  queueEntityName: string,
  queueName: string,
  viewerEntity: string,
  searchString?: string
) => {
  const isPublicQueue =
    queueName === WANDB_PUBLIC_QUEUE_NAME &&
    queueEntityName === WANDB_ENTITY_NAME;

  const shouldLoadViewerProjects =
    isPublicQueue && viewerEntity !== WANDB_ENTITY_NAME;

  // TODO: Filter destination projects serverside so we can
  // fetch more projects.
  const userProjectQuery = useViewerAllProjectsWithPatternQuery({
    variables: {
      pattern: searchString ?? '',
    },
    skip: !shouldLoadViewerProjects || !viewerEntity,
  });

  const query = useAllProjectNamesWithPatternQuery({
    variables: {
      entityName: queueEntityName,
      pattern: searchString ?? '',
    },
    skip: !queueEntityName || shouldLoadViewerProjects,
  });

  if (shouldLoadViewerProjects) {
    // Load the viewer's projects so they can launch a public job into
    // their own project.
    const projectEdges = userProjectQuery.data?.viewer?.projects?.edges ?? [];
    const projectOptions = processProjectOptions(projectName, projectEdges);

    const groupedProjectOptions = _.groupBy(projectOptions, 'entityName');
    const entityGroupOptions = Object.entries(groupedProjectOptions).map(
      ([entityName, options]) => ({
        label: entityName,
        options,
      })
    );

    return {
      options: entityGroupOptions,
      loading: userProjectQuery.loading,
      error: userProjectQuery.error,
    };
  } else {
    const projectEdges = query.data?.projects?.edges ?? [];
    const projectOptions = processProjectOptions(projectName, projectEdges);

    const entityGroupOption = {
      label: queueEntityName,
      options: projectOptions,
    };

    return {
      options: [entityGroupOption],
      loading: query.loading,
      error: query.error,
    };
  }
};

export const useRunInfoMapFromQueues = (queueOrQueues: Queue | Queue[]) => {
  const [runInfoMap, setRunInfoMap] = useState<RunInfoMap | null>(null);

  const queues = useMemo(
    () => (queueOrQueues instanceof Array ? queueOrQueues : [queueOrQueues]),
    [queueOrQueues]
  );

  // Hack to force polling, since run info may
  // change independently of queues
  const [pollCount, setPollCount] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setPollCount(pollCount + 1);
    }, 10000);
    return () => clearInterval(interval);
  }, [pollCount]);

  useEffect(() => {
    let mounted = true;
    getRunInformation(getProjectRunIdMap(queues))
      .then(map => {
        if (mounted) {
          setRunInfoMap(map);
        }
      })
      .catch(e => {
        if (mounted) {
          console.error(e);
          setRunInfoMap({});
        }
      });

    return () => {
      mounted = false;
    };
  }, [pollCount, queues]); // N.B. pollCount for trigger only.

  return runInfoMap;
};

const getJobOptions = (data: FetchProjectJobsPartialQuery): JobOption[] => {
  const options = [];
  const collectionEdges =
    data?.project?.artifactType?.artifactCollections?.edges ?? [];
  for (const collectionEdge of collectionEdges) {
    // Can only run a Job if it has versions associated with it.
    // It might not if it was created through the Artifacts UI.
    const artifactCollection = collectionEdge.node;
    const versionEdges = artifactCollection?.artifacts?.edges ?? [];
    if (versionEdges.length && data.project?.name) {
      // These options come from the project's jobs, so the project's
      // entity will be used downstream.
      options.push({
        project: data.project.name,
        value: artifactCollection?.id ?? '',
        label: artifactCollection?.name ?? '',
      });
    }
  }
  return _.sortBy(options, 'label');
};

const getJobOptionsForEntity = (
  data: FetchEntityJobsQuery,
  filterForPublicQueue?: boolean
) => {
  const options = [];
  const projectEdges = data?.entity?.projects?.edges ?? [];

  for (const projectEdge of projectEdges) {
    const projectName = projectEdge?.node?.name;

    // For public queue queries, only process the 'wandb/jobs' project
    if (filterForPublicQueue) {
      if (!isWandbJobsProject(data.entity?.name ?? '', projectName ?? '')) {
        continue; // Skip non-wandb/jobs projects for public queue
      }
    }

    const collectionEdges =
      projectEdge?.node?.artifactType?.artifactCollections?.edges ?? [];

    for (const collectionEdge of collectionEdges) {
      const artifactCollection = collectionEdge.node;

      // For public queue queries, only include jobs that are explicitly allowed
      if (filterForPublicQueue) {
        if (
          !isAllowedWandbPublicJob(
            artifactCollection?.name,
            projectEdge?.node?.name,
            data.entity?.name
          )
        ) {
          continue; // Skip jobs not allowed for public queue
        }
      }

      const versionEdges = artifactCollection?.artifacts?.edges ?? [];
      if (versionEdges.length && projectName != null && data.entity?.name) {
        const label =
          artifactCollection?.name != null
            ? `${projectName}/${artifactCollection.name}`
            : '';
        options.push({
          entity: data.entity.name,
          project: projectName,
          value: artifactCollection?.id ?? '',
          label: filterForPublicQueue ? `${data.entity?.name}/${label}` : label,
        });
      }
    }
  }
  return options;
};

const INCREMENTAL_BATCH_SIZE = 20;
const INCREMENTAL_MAX_RETRIES = 10;
const INCREMENTAL_MAX_EBO_SCALE = 6;
export interface FetchProjectJobsResult {
  data: FetchProjectJobsPartialQueryResult['data'];
  error: Error | null;
  loading: boolean; // Are we loading initial data?
  loadingMore: boolean; // Are we loading more data?
  refetch: () => void;
}
export const useFetchProjectJobsIncrementally = (
  entityName: string,
  projectName: string
): FetchProjectJobsResult => {
  // Fetched naively, this query can be quite expensive because it can potentially
  // fan out to 500 artifact collections with 500 artifacts each, based on
  // current backend limits.  We fetch incrementally to avoid this, effectively
  // amortizing the cost across multiple requests. Also allows the backend to
  // correctly return more than 500 artifact collections.  Batch size should be
  // tuned such that we process ~1RPS for the sake of app liveness.

  const lastPageInfo = useRef<PageInfo | undefined>(undefined);
  const retries = useRef(0);
  const mounted = useRef(true);

  const [result, setResult] =
    useState<FetchProjectJobsResult['data']>(undefined);
  const [err, setErr] = useState<Error | null>(null);

  const [fetchJobsIncrementally, {loading: loadingMore}] = useLazyQuery(
    FetchProjectJobsPartialDocument,
    {
      onError: e => {
        if (!mounted.current) {
          return;
        }

        if (e.networkError == null) {
          // Treat gql errors as fatal
          setErr(e);
          return;
        }

        // On net error only, retry with exponential backoff, up to 64 seconds/retry
        retries.current++;
        if (retries.current > INCREMENTAL_MAX_RETRIES) {
          setErr(
            new Error(
              `Max retries exceeded (${retries.current}). Last error: ${e.networkError.message}`
            )
          );
          return;
        }

        setTimeout(() => {
          if (!mounted.current) {
            return;
          }

          fetchJobsIncrementally({
            variables: {
              entityName,
              projectName,
              limit: INCREMENTAL_BATCH_SIZE,
              after: lastPageInfo.current?.endCursor ?? '',
            },
          });
        }, 1000 * 2 ** Math.min(retries.current, INCREMENTAL_MAX_EBO_SCALE));
      },
      onCompleted: data => {
        if (!mounted.current) {
          return;
        }

        retries.current = 0;
        const newItems =
          data.project?.artifactType?.artifactCollections?.edges ?? [];
        setResult(old => {
          // First result, return the data as usual
          if (old == null) {
            return data;
          }

          // Subsequent results, merge the new collections into existing data
          return produce(old, draft => {
            draft?.project?.artifactType?.artifactCollections?.edges.push(
              ...newItems
            );
          });
        });
        lastPageInfo.current =
          data.project?.artifactType?.artifactCollections.pageInfo;
        if (lastPageInfo.current?.hasNextPage) {
          fetchJobsIncrementally({
            variables: {
              entityName,
              projectName,
              limit: INCREMENTAL_BATCH_SIZE,
              after: lastPageInfo.current?.endCursor ?? '',
            },
          });
        }
      },
      variables: {
        entityName,
        projectName,
        limit: INCREMENTAL_BATCH_SIZE,
        after: '',
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  const fetchFresh = () => {
    setResult(undefined);
    fetchJobsIncrementally();
  };

  // only run on first mount or project changes
  useEffect(() => {
    fetchFresh();
    return () => {
      mounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityName, projectName]);

  return {
    error: err,
    data: result,
    loading: result == null,
    loadingMore,
    refetch: fetchFresh,
  };
};

export const useAvailableJobOptions = (
  entityName: string,
  projectName: string
): {jobOptions: JobOption[]; loading: boolean} => {
  const isInEntityLaunchProject = projectName === DEFAULT_LAUNCH_PROJECT_NAME;
  const projectJobsQuery = useFetchProjectJobsIncrementally(
    entityName,
    projectName
  );
  const entityJobsQuery = useFetchEntityJobsQuery({
    variables: {
      entityName,
    },
    skip: !isInEntityLaunchProject,
  });

  const isInWandbPublicJobProject =
    projectName === WANDB_JOBS_PROJECT_NAME && entityName === WANDB_ENTITY_NAME;
  // Do not query for public jobs if the user is already in the public jobs
  // project.
  const publicJobsQuery = useFetchEntityJobsQuery({
    variables: {
      entityName: WANDB_ENTITY_NAME,
      pattern: WANDB_JOBS_PROJECT_NAME,
    },
    skip: isInWandbPublicJobProject,
  });

  const noResults =
    projectJobsQuery.loading ||
    projectJobsQuery.error ||
    entityJobsQuery.loading ||
    entityJobsQuery.error ||
    publicJobsQuery.loading ||
    publicJobsQuery.error;

  const entityData = entityJobsQuery.data;
  const projectData = projectJobsQuery.data;
  const publicJobsData = publicJobsQuery.data;

  return useMemo(() => {
    if (noResults) {
      return {jobOptions: [] as JobOption[], loading: true};
    }
    const jobOptions = isInEntityLaunchProject
      ? [
          ...getJobOptionsForEntity(publicJobsData!, true),
          ...getJobOptionsForEntity(entityData!),
        ]
      : [
          ...getJobOptionsForEntity(publicJobsData!, true),
          ...getJobOptions(projectData!),
        ];

    return {
      jobOptions,
      loading: false,
    };
  }, [
    noResults,
    isInEntityLaunchProject,
    entityData,
    projectData,
    publicJobsData,
  ]);
};

export const useFetchRunJob = ({
  entityName,
  projectName,
  runName,
  skip,
}: {
  entityName: string;
  projectName: string;
  runName: string;
  skip: boolean;
}) => {
  const runJobQuery = useRunJobQuery({
    variables: {
      projectName,
      entityName,
      runName,
    },
    skip,
  });

  const job = runJobQuery.data?.project?.run?.job;
  return useMemo(() => {
    return {
      jobInfo:
        job == null
          ? null
          : {
              id: job.artifactSequence.id,
              versionStr: `v${job.versionIndex}`,
              name: job.artifactSequence.name,
              versionedJobName: `${job.artifactSequence.name}:v${job.versionIndex}`,
            },
      loading: runJobQuery.loading,
      error: runJobQuery.error,
    };
  }, [job, runJobQuery.loading, runJobQuery.error]);
};

export const useRunQueueItem = (
  entityName: string,
  projectName: string,
  runQueueName: string,
  runQueueItemID: string
) => {
  const {data, loading, error} = useFetchRunQueueItemFromProjectQuery({
    variables: {
      entityName,
      projectName,
      runQueueName,
      runQueueItemID,
    },
    skip: !runQueueItemID || !runQueueName,
    context: propagateErrorsContext(),
  });

  return useMemo(() => {
    return {
      runQueueItem:
        loading || error
          ? null
          : {
              ...data?.project?.runQueue?.runQueueItem,
              queueId: data?.project?.runQueue?.id,
            },
      loading,
      error,
    };
  }, [
    loading,
    error,
    data?.project?.runQueue?.runQueueItem,
    data?.project?.runQueue?.id,
  ]);
};

// Helper function to process project edges into options
const processProjectOptions = (
  currentProjectName: string,
  projectEdges: any[]
) => {
  const projectSort = sortSpecialFirst(currentProjectName, a => a.projectName);
  return projectEdges
    .map((projectEdge: ProjectEdge) => {
      const projectId = projectEdge.node?.id ?? '';
      const projectLabel = projectEdge.node?.name ?? '';
      const projectEntityName = projectEdge.node?.entityName ?? '';

      return {
        value: `${projectEntityName}/${projectLabel}`,
        entityName: projectEntityName,
        projectId,
        projectName: projectLabel,
      };
    })
    .sort(projectSort);
};

export const usePublicQueueAccess = () => {
  const orgRampFlagQuery = useViewerOrganizationFeatureFlagsQuery({
    variables: {rampIDType: RampIdType.OrgId},
  });

  const {
    data,
    loading: isFeatureFlagLoading,
    error: featureFlagQueryError,
  } = orgRampFlagQuery;

  const hasPublicQueueAccess = data?.viewer?.organizations.some(org => {
    if (!org?.featureFlags) {
      return false;
    }
    const {featureFlags} = org;
    return featureFlags.find(
      flag => flag?.rampKey === PUBLIC_QUEUE_RAMP_KEY && flag?.isEnabled
    );
  });

  return {hasPublicQueueAccess, isFeatureFlagLoading, featureFlagQueryError};
};

export const useUserEnhancedQueueItems = (queueOrQueues: Queue | Queue[]) => {
  const viewer = useViewer();
  const runInfoMap = useRunInfoMapFromQueues(queueOrQueues);

  // Get user's last 100 runs
  const {data: userRunsData, loading: userRunsLoading} = useUserRunsQuery({
    variables: {
      username: viewer?.username ?? '',
      first: 100,
    },
    pollInterval: 5000,
  });

  const queues = useMemo(
    () => (queueOrQueues instanceof Array ? queueOrQueues : [queueOrQueues]),
    [queueOrQueues]
  );

  const sortedItems = useMemo(() => {
    if (!runInfoMap || !userRunsData?.user?.runs?.edges || !viewer) {
      return [];
    }

    const FIVE_MINUTES_MS = 5 * 60 * 1000;
    const fiveMinutesAgo = Date.now() - FIVE_MINUTES_MS;

    const activeUserRuns = userRunsData.user.runs.edges
      .filter(edge => {
        const node = edge.node;

        if (node.state === 'running') {
          return true;
        }

        const isTerminalStatus = ['killed', 'crashed', 'finished'].includes(
          node.state ?? ''
        );

        if (isTerminalStatus && node.updatedAt) {
          const updatedAtTimestamp = new Date(node.updatedAt).getTime();
          return updatedAtTimestamp > fiveMinutesAgo;
        }

        return false;
      })
      .map(
        (edge): RunInfoWithProjectAndEntityNames => ({
          id: edge.node.id,
          name: edge.node.name,
          displayName: edge.node.displayName ?? '',
          state: edge.node.state ?? '',
          createdAt: edge.node.createdAt,
          heartbeatAt: edge.node.heartbeatAt ?? '',
          entityName: edge.node.project?.entityName ?? '',
          projectName: edge.node.project?.name ?? '',
        })
      );

    const userRunsNameSet = new Set(
      userRunsData.user.runs.edges.map(edge => edge.node.name)
    );

    const userRunQueueItems: RunQueueItem[] = [];
    for (const queue of queues) {
      const filteredItems = queue.items.filter(item => {
        if (item.author !== viewer.username) {
          return false;
        }

        // Filter out run queue items that aren't recently enqueued and aren't
        // in the most recent 100 user runs.
        const notInRecentUserRuns =
          item.associatedRunId && !userRunsNameSet.has(item.associatedRunId);

        if (notInRecentUserRuns && !isRecentlyEnqueued(item)) {
          return false;
        }

        if (userRunsNameSet.has(item.associatedRunId ?? '')) {
          return false;
        }

        // Handle "orphaned" run queue items. An orphaned run queue item is the
        // result of deleting a run that was created by a launch job. This is a
        // limitation of our state machine as it's ambiguous whether or not the
        // claimed run queue item is still in the process of creating a run.
        const run =
          item.associatedRunId && runInfoMap[item.associatedRunId]
            ? runInfoMap[item.associatedRunId]
            : null;

        const orphaned =
          item.state === 'CLAIMED' &&
          !run &&
          item.updatedAt &&
          new Date(item.updatedAt).getTime() < fiveMinutesAgo;

        if (orphaned) {
          return false;
        }

        return true;
      });

      const enhancedItems = enhanceRunQueueItems(
        filteredItems,
        queue,
        runInfoMap
      );
      userRunQueueItems.push(...enhancedItems);
    }

    const {sortedActiveRuns, sortedRunQueueItems} =
      sortUserRunQueueItemsWithRecentRuns(
        userRunQueueItems,
        activeUserRuns,
        userRunsNameSet
      );

    return mapToPopoverItems(sortedActiveRuns, sortedRunQueueItems);
  }, [runInfoMap, userRunsData, viewer, queues]);

  return {
    items: sortedItems,
    loading: !runInfoMap || userRunsLoading,
    userRuns: userRunsData?.user?.runs?.edges || [],
  };
};

// when populating the target entity and project name, consider that users can
// now launch jobs from anywhere into any project (with access).
//
// use the entity and project of the page they're currently on if they
// have access.
export const usePageProjectAndEntityTarget = () => {
  let targetEntityName = '';
  let targetProjectName = '';
  const {projectName: currentProjectName, entityName: currentEntityName} =
    useProjectParams();

  const viewer = useViewer();
  const viewerTeams: string[] =
    viewer?.teams?.edges?.map(e => e.node.name) ?? [];
  const viewerHasAccessToProject =
    currentEntityName === viewer?.entity ||
    (viewerTeams != null && viewerTeams.includes(currentEntityName));

  if (viewerHasAccessToProject) {
    targetEntityName = currentEntityName;
    targetProjectName = currentProjectName;
  }

  return {
    targetEntityName,
    targetProjectName,
  };
};
